import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';

import { useBankFeedRouteContext } from 'pages/company/BankFeedRoute/context';
import { deriveCurrencyOptions } from 'pages/company/BankFeedRoute/currencyOptions';
import { computeEmptyVariant } from 'pages/company/BankFeedRoute/components/EmptyStates/computeEmptyVariant';

const CURRENCY_SELECT_WIDTH = 120;

// Currency Select — shared by both per-tab toolbars. Options derive from the selected
// accounts' currencies (DA-19365); value="" is held (with a hidden empty MenuItem, no
// out-of-range warning) while summary loads or before the parent seeds the default
// currency. Reads everything from the route context, so it is identical on both tabs.
const CurrencyFilter: React.FC = () => {
  const intl = useIntl();
  const { toolbarFilters, setToolbarFilters, accountType, summary, isSummaryLoading } = useBankFeedRouteContext();

  const currencyOptions = useMemo(
    () => deriveCurrencyOptions(summary, accountType, toolbarFilters.accountIds),
    [summary, accountType, toolbarFilters.accountIds],
  );

  // Pre-data empty states (no-connections / no-transactions): the grid is not mounted, so
  // render the control disabled and blank rather than populated with its default.
  const isEmptyView = computeEmptyVariant(summary, accountType) !== null;
  const isCurrencyReady = !isSummaryLoading && currencyOptions.length > 0 && toolbarFilters.currency != null;

  const handleCurrencyChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const next = event.target.value;
      setToolbarFilters((prev) => ({ ...prev, currency: next }));
    },
    [setToolbarFilters],
  );

  const currencyLabel = intl.formatMessage({
    id: 'company.bankFeed.transactions.toolbar.currency.label',
    defaultMessage: 'Currency',
  });

  // Show the selected currency once it is actually selectable; otherwise the blank in-range
  // value (see the Select's value comment below).
  const currencyValue = isCurrencyReady && toolbarFilters.currency ? toolbarFilters.currency : '';

  return (
    <FormControl
      size="small"
      sx={{ width: CURRENCY_SELECT_WIDTH, backgroundColor: 'common.white' }}
      disabled={isEmptyView || !isCurrencyReady}
    >
      <InputLabel id="bank-feed-currency-select-label">{currencyLabel}</InputLabel>
      <Select<string>
        labelId="bank-feed-currency-select-label"
        // value="" is held while summary loads or before the parent effect seeds the default
        // currency; the hidden empty MenuItem keeps it in-range (no MUI out-of-range warning)
        // and the label rests in place. The disabled state + spinner adornment communicate
        // loading — there is no separate "Loading…" menu row.
        value={currencyValue}
        label={currencyLabel}
        onChange={handleCurrencyChange}
        startAdornment={
          isSummaryLoading ? (
            <InputAdornment position="start">
              <CircularProgress size={16} />
            </InputAdornment>
          ) : undefined
        }
      >
        <MenuItem value="" sx={{ display: 'none' }} />
        {currencyOptions.map((c) => (
          <MenuItem key={c} value={c}>
            {c}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CurrencyFilter;
